import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  activateMiniGamePlayer,
  clearStoredGameLaunch,
  isGameLaunchLockedByAnotherWindow,
  readStoredGameLaunch,
  releaseGameLaunchLock,
  startGameLaunchHeartbeat,
} from "../../../lib/gameLaunch";
import { GamePlayerGlobalStyle, StyledGamePlayerPage } from "./StyledGamePlayerPage";
import { requestWalletSummarySync } from "../../../utils/walletSync";

const getPlayerKindLabel = (kind) => {
  if (kind === "provider") return "벤더 로비";
  if (kind === "game") return "게임 실행";
  return "게임 실행";
};

const GamePlayerPage = () => {
  const navigate = useNavigate();
  const [isFrameLoading, setIsFrameLoading] = useState(true);
  const [playerMode, setPlayerMode] = useState("default");
  const launch = useMemo(() => readStoredGameLaunch(), []);
  const [lockConflict, setLockConflict] = useState(() =>
    launch?.playUrl ? isGameLaunchLockedByAnotherWindow() : false
  );
  const keepLaunchAliveRef = useRef(false);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    document.documentElement.classList.add("crypto-game-player-route");
    document.body.classList.add("crypto-game-player-route");

    return () => {
      document.documentElement.classList.remove("crypto-game-player-route");
      document.body.classList.remove("crypto-game-player-route");
      document.documentElement.classList.remove("crypto-game-player-theater");
      document.body.classList.remove("crypto-game-player-theater");
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const shouldUseTheater = playerMode === "theater";
    document.documentElement.classList.toggle("crypto-game-player-theater", shouldUseTheater);
    document.body.classList.toggle("crypto-game-player-theater", shouldUseTheater);

    return () => {
      document.documentElement.classList.remove("crypto-game-player-theater");
      document.body.classList.remove("crypto-game-player-theater");
    };
  }, [playerMode]);

  useEffect(() => {
    if (!launch?.playUrl) {
      releaseGameLaunchLock();
      return undefined;
    }

    const heartbeat = startGameLaunchHeartbeat(launch, { releaseOnCleanup: false });
    if (!heartbeat.success) {
      setLockConflict(true);
    }

    return () => {
      heartbeat.cleanup();
      if (!keepLaunchAliveRef.current) {
        releaseGameLaunchLock();
      }
    };
  }, [launch]);

  const handleExit = () => {
    const sourcePath = launch?.sourcePath && launch.sourcePath !== "/play" ? launch.sourcePath : "/";
    clearStoredGameLaunch();
    releaseGameLaunchLock();
    requestWalletSummarySync("game-player-exit");
    navigate(sourcePath, { replace: true });
  };

  const handleChangeMode = (nextMode) => {
    if (nextMode === "mini") {
      keepLaunchAliveRef.current = true;
      activateMiniGamePlayer();
      const sourcePath =
        launch?.sourcePath && launch.sourcePath !== "/play" ? launch.sourcePath : "/";
      navigate(sourcePath, { replace: true });
      return;
    }

    setPlayerMode((currentMode) => (currentMode === nextMode ? "default" : nextMode));
  };

  if (!launch?.playUrl || lockConflict) {
    return (
      <StyledGamePlayerPage>
        <GamePlayerGlobalStyle />
        <div className="game-player-empty">
          <div>
            <h1>{lockConflict ? "이미 실행 중인 게임이 있습니다." : "실행할 게임 정보가 없습니다."}</h1>
            <p>
              {lockConflict
                ? "다른 창의 게임을 종료한 뒤 다시 실행해주세요."
                : "게임 목록에서 다시 실행해주세요."}
            </p>
            <button className="game-player-action primary" type="button" onClick={handleExit}>
              홈으로 이동
            </button>
          </div>
        </div>
      </StyledGamePlayerPage>
    );
  }

  const title = launch.label || "게임";
  const vendorText = launch.vendorName || getPlayerKindLabel(launch.kind);

  return (
    <StyledGamePlayerPage>
      <GamePlayerGlobalStyle />
      <div className={`game-player-shell is-${playerMode}`}>
        <section className="game-player-stage" aria-label={`${title} 실행 화면`}>
          <iframe
            allow="fullscreen; autoplay; clipboard-read; clipboard-write; encrypted-media; payment"
            allowFullScreen
            className="game-player-frame"
            onLoad={() => setIsFrameLoading(false)}
            referrerPolicy="no-referrer-when-downgrade"
            src={launch.playUrl}
            title={`${title} 실행 화면`}
          />
          <div
            className={`game-player-loading ${isFrameLoading ? "" : "is-hidden"}`}
            aria-live="polite"
          >
            <div className="game-player-loading-card">
              <span className="game-player-loading-mark" aria-hidden="true" />
              <span>게임 화면을 불러오는 중입니다.</span>
            </div>
          </div>
        </section>

        <section className="game-player-controlbar" aria-label="게임 실행 도구">
          <div className="game-player-title">
            <span>{vendorText}</span>
            <h1>{title}</h1>
          </div>

          <div className="game-player-actions">
            <button
              className={`game-player-action mode ${playerMode === "mini" ? "is-active" : ""}`}
              type="button"
              onClick={() => handleChangeMode("mini")}
            >
              미니플레이어
            </button>
            <button
              className={`game-player-action mode ${playerMode === "theater" ? "is-active" : ""}`}
              type="button"
              onClick={() => handleChangeMode("theater")}
            >
              {playerMode === "theater" ? "기본모드" : "영화관모드"}
            </button>
          </div>
        </section>
      </div>
      <button
        aria-label="모바일 게임 종료"
        className="game-player-mobile-exit"
        type="button"
        onClick={handleExit}
      >
        게임 종료
      </button>
    </StyledGamePlayerPage>
  );
};

export default GamePlayerPage;
